import React, { useEffect, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import Detail from "./Detail";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import eventEmitter from "../utils/eventEmitter";
import {
  getPrimaryField as getPrimaryFieldHelper,
  isInvalid as isInvalidHelper,
  buildSaveDiscardText as buildSaveDiscardTextHelper,
} from "../utils/helper";

let _lastAction = null;
let passwords = [];
/**
 * Detail wrapper component
 * @module
 * @param {func} onSubmit - On form submit function
 * @param {detailSchema[]} schema - Detail Schema
 * @param {object} data - Data
 * @param {func} onChange - On field change function
 * @param {func} onDiscard - On discard changes function
 * @param {func} onBlur - On field blur function
 * @param {object} parentProps - Parent props
 * @param {bool} disabled - Read only detail
 * @param {number} akcDisabled - Disabled actions mask number
 * @param {number} akcHidden - Hidden actions mask number
 * @param {bool} isLoading - Is loading
 * @param {array} ui - Custom schema
 * @param {object} args - Custom arguments
 * @param {element} toolbarActions - Toolbar actions
 * @param {string} primaryField - Primary field
 * @param {func} onAdd - On entry add function
 * @param {func} onCreate - On entry create function
 * @param {func} onUpdate - On entry update function
 * @param {func} onUpload - On file upload function
 * @param {bool} toolbarHidden - Toolbar hidden
 * @param {func} onGet - On data get function
 * @param {object} allData - All data
 * @param {element} currentEl - Current element
 * @param {func} next - Switches to the next entry function
 * @param {func} previous - Switches to the previous entry function
 * @param {bool} modified - Is current entry modified
 * @param {string} discardText - Discard text
 * @param {string} saveChangesDialogText - SaveChangesDialog text
 * @param {bool} isPicker - Is field picker
 * @param {bool} hiddenBack - Hidden back arrow in detail toolbar
 * @param {string} title - Detail title
 * @param {array} pages - Pages
 * @param {func} componentFactory - Factory for custom components
 * @param {bool} hiddenSave - Hidden save button in detail toolbar
 * @param {bool} sidebarHorizontal - Should sidebar be horizontal?
 * @param {string} [db] - db
 */

const DetailWrapper = forwardRef((props, ref) => {
  const [isOpenDiscard, setIsOpenDiscard] = useState(false);
  const [isOpenSaveChangesDialog, setIsOpenSaveChangesDialog] = useState(false);
  const [id, setId] = useState(0);
  const [dataInvalid, setDataInvalid] = useState(false);
  const [invalidFields, setInvalidFields] = useState([]);
  const [invalidRecommended, setInvalidRecommended] = useState([]);

  //create ref with funcions
  useImperativeHandle(ref, () => ({
    validateFields: () => validateFields(),
    validateRecommendedFields: () => validateRecommendedFields(),
    isInvalid: (field, value) => isInvalid(field, value),
    getValue: (field) => getValue(field),
    customValidation_: (field) => customValidation_(field),
    addInvalidClass: (field) => addInvalidClass(field),
    removeInvalidClass: (field) => removeInvalidClass(field),
    handleBlur: (field, value) => handleBlur(field, value),
    handleSubmit: (event) => handleSubmit(event),
    props,
    ref
  }));

  const getPrimaryField = useCallback(() => {
    return getPrimaryFieldHelper(props.primaryField);
  }, [props.primaryField]);

  const removeInvalidClass = useCallback((field) => {
    let doc = document;
    if (props.parentClassName) {
      doc = document.getElementsByClassName(props.parentClassName)[0];
    }
    let element = doc.getElementsByClassName(`form-field-${field.field}`)[0];
    if (element) {
      removeFieldClass(element);
    }
  }, [props.parentClassName]);

  const removeInvalidClasses = useCallback(() => {
    props.schema.forEach((field) => {
      removeInvalidClass(field);
    });
  }, [props.schema, removeInvalidClass]);

  useEffect(() => {
    passwords = props.schema.filter((x) => x.type === "password")
      .map((x) => ({ ...x, value: undefined }))
  }, [props.schema]);

  useEffect(() => {
    if (props.data && props.data[getPrimaryField()] !== id) {
      removeInvalidClasses();
      setId(props.data[getPrimaryField()]);
    }
  }, [props.data, getPrimaryField, id, removeInvalidClasses]);

  const passwordValidation = async (field, value) => {
    let arePasswordsValid = true;

    passwords = passwords.map((item) =>
      item.field === field.field
        ? { ...item, value }
        : { ...item, value: props.data[item.field] }
    )

    //const filteredPasswords = passwords.filter(item => item.value);

    let passwordFields = props.schema.filter(
      (item) => item.type === "password"
    );

    let firstInvalid = await isInvalid(passwordFields[0], passwords[0].value);

    if (firstInvalid) {
      addInvalidClass(passwordFields[0]);
      arePasswordsValid = false;
    } else {
      removeInvalidClass(passwordFields[0]);
    }
    if (!firstInvalid && passwords.length === 2) {
      if (passwords[0].value !== passwords[1].value) {
        addInvalidClass(passwordFields[1]);
        arePasswordsValid = false;
      } else {
        removeInvalidClass(passwordFields[1]);
      }
    }
    return arePasswordsValid;
  };

  const isInvalid = async (field, value) => {
    if (field.disabled) {
      return false;
    }

    const inEdit = props.data[getPrimaryField()] !== null;

    return await isInvalidHelper(field, value, inEdit, props.data);
  };

  const handleOpenDiscard = () => {
    setIsOpenDiscard(true);
  };

  const handleCloseDiscard = () => {
    setIsOpenDiscard(false);
  };

  const handleConfirmDiscard = () => {
    switch (_lastAction) {
      case "next":
        next();
        break;
      case "previous":
        previous();
        break;
      case "back":
        back();
        break;
      default:
        break;
    }

    handleCloseDiscard();
  };

  const handleOpenSaveChangesDialog = () => {
    setIsOpenSaveChangesDialog(true);
  };

  const handleCloseSaveChangesDialog = () => {
    setIsOpenSaveChangesDialog(false);
  };

  const handleCancelSaveChangesDialog = () => {
    switch (_lastAction) {
      case "next":
        next();
        break;
      case "previous":
        previous();
        break;
      case "back":
        back();
        break;
      default:
        break;
    }

    handleCloseSaveChangesDialog();
  };

  const handleConfirmSaveChangesDialog = async () => {
    await handleSubmit(new Event("submit"));

    handleCancelSaveChangesDialog();
  };

  const onSubmit = async () => {
    const {
      data,
      primaryField,
      onUpdate,
      onCreate,
      onSubmit: onSubmitProps,
    } = props;

    const id = data[getPrimaryFieldHelper(primaryField)];
    if (id) {
      await onUpdate(id, data);
    } else {
      await onCreate(data);
    }

    // TODO: OVERIT
    onSubmitProps();
  };

  const tryNext = () => {
    if (props.modified) {
      _lastAction = "next";
      if (props.parentProps && props.parentProps[props.primaryField]) {
        handleOpenSaveChangesDialog();
      } else {
        handleOpenDiscard();
      }
      return;
    }

    next();
  };

  const tryPrevious = () => {
    if (props.modified) {
      _lastAction = "previous";
      if (props.parentProps && props.parentProps[props.primaryField]) {
        handleOpenSaveChangesDialog();
      } else {
        handleOpenDiscard();
      }
      return;
    }

    previous();
  };


  const back = () => {
    eventEmitter.changeTab(props.primaryField, 0);

    const ID = props.data[getPrimaryField()];
    if (ID) {
      eventEmitter.changeSelected(
        [{ [getPrimaryField()]: ID }],
        getPrimaryField()
      );
    } else {
      if (props.onDiscard) {
        props.onDiscard();
      }
    }

    removeInvalidClasses();
  };

  const previous = () => {
    if (props.prev) {
      props.prev();
    }
    let ID = props.data[getPrimaryField()];
    let minID = props.allData[0][getPrimaryField()];
    let index = props.allData.findIndex(
      (item) => item[getPrimaryField()] === ID
    );
    if (ID !== minID) {
      const previousId = props.allData[index - 1][getPrimaryField()];
      // props.onGet(previousId); -> onGet sa zavola o riadok nizsie pri onSelect
      eventEmitter.changeSelected(
        [{ [getPrimaryField()]: previousId }],
        getPrimaryField()
      );
      removeInvalidClasses();
    }
  };

  const next = () => {
    if (props.next) {
      props.next();
    }
    let ID = props.data[getPrimaryField()];
    let maxID = props.allData[props.allData.length - 1][getPrimaryField()];
    let index = props.allData.findIndex(
      (item) => item[getPrimaryField()] === ID
    );
    if (ID !== maxID) {
      const nextId = props.allData[index + 1][getPrimaryField()];
      // props.onGet(nextId); -> onGet sa zavola o riadok nizsie pri onSelect
      eventEmitter.changeSelected(
        [{ [getPrimaryField()]: nextId }],
        getPrimaryField()
      );
      removeInvalidClasses();
    }
  };

  const disabledNext = () => {
    let ID = props.data ? props.data[getPrimaryField()] : null;
    let data = props.allData ? props.allData : [];
    let index = data?.findIndex((item) => item[getPrimaryField()] === ID);
    if (index < data.length - 1) {
      return false;
    } else {
      return true;
    }
  };

  const handleBack = (event) => {
    if (event === undefined && props.modified) {
      _lastAction = "back";
      if (props.parentProps && props.parentProps[props.primaryField]) {
        handleOpenSaveChangesDialog();
      } else {
        handleOpenDiscard();
      }
      return;
    }

    eventEmitter.changeTab(props.primaryField, 0);
  };

  const disabledPrevious = () => {
    let ID = props.data ? props.data[getPrimaryField()] : null;
    let data = props.allData ? props.allData : [];
    let index = data.findIndex((item) => item[getPrimaryField()] === ID);
    if (index > 0) {
      return false;
    } else {
      return true;
    }
  };

  const validateFields = async () => {
    let submit = true;
    let invalidFields = [];
    for (let field of props.schema) {
      const value = getValue(field.field);
      if (field.type === "password") {
        let passwordSubmit = await passwordValidation(field, value);
        submit = submit ? passwordSubmit : submit;
        continue;
      }
      if (await isInvalid(field, value) || (await customValidation_(field))) {
        submit = false;
        invalidFields = [...invalidFields, field.title];
        addInvalidClass(field);
      } else if (isRecommendedInvalid(field, value)) {
        addInvalidRecommendedClass(field);
      } else {
        addValidClass(field);
      }
    }
    setInvalidFields(invalidFields);
    return submit;
  };

  const validateRecommendedFields = async () => {
    let invalid = false;
    let invalidRecommended = [];
    for (let field of props.schema) {
      if (isRecommendedInvalid(field, getValue(field.field))) {
        invalidRecommended = [...invalidRecommended, field];
        invalid = true;
      }
    }
    setInvalidRecommended(invalidRecommended);
    return invalid;
  };

  const isRecommendedInvalid = (field, value) => {
    if (!field.recommended) {
      return false;
    }
    if (field.disabled) {
      return false;
    }

    return !(value || value === 0 || value === false) || /^\s+$/gm.test(value);
  };

  const prepareSubmit = async () => {
    if (props.parentProps && props.parentProps.isPicker) {
      await onSubmit();
      props.parentProps.applyFilter(props.data[getPrimaryField()]);
    } else {
      await onSubmit();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (await validateFields()) {
      if (await validateRecommendedFields()) {
        return false;
      }
      await prepareSubmit();
      removeInvalidClasses();
      return true;
    } else {
      setDataInvalid(true);
      setTimeout(() => {
        setDataInvalid(false);
      }, 4500);
    }
    return false;
  };

  const removeFieldClass = (element) => {
    element.classList.remove("validation-valid");
    element.classList.remove("recommended-invalid");
    element.classList.remove("validation-invalid");
  };

  const addInvalidRecommendedClass = (field) => {
    let doc = document;
    if (props.parentClassName) {
      doc = document.getElementsByClassName(props.parentClassName)[0];
    }
    let element = doc.getElementsByClassName(`form-field-${field.field}`)[0];
    if (element) {
      removeFieldClass(element);
      element.classList.add("recommended-invalid");
    }
  };

  const addInvalidClass = (field) => {
    let doc = document;
    if (props.parentClassName) {
      doc = document.getElementsByClassName(props.parentClassName)[0];
    }
    let element = doc.getElementsByClassName(`form-field-${field.field}`)[0];
    if (element) {
      removeFieldClass(element);
      element.classList.add("validation-invalid");
    }
  };

  const addValidClass = (field) => {
    let doc = document;
    if (props.parentClassName) {
      doc = document.getElementsByClassName(props.parentClassName)[0];
    }
    let element = doc.getElementsByClassName(`form-field-${field.field}`)[0];
    if (element) {
      removeFieldClass(element);
      element.classList.add("validation-valid");
    }
  };

  const customValidation_ = async (field) => {
    if (props.customValidation) {
      return await props.customValidation(field);
    }

    return false;
  };

  const handleBlur = async (field, value) => {
    const v = value !== null ? value : getValue(field.field);

    if (field.type === "password") {
      await passwordValidation(field, v);
      return;
    }

    if (await isInvalid(field, v) || (await customValidation_(field))) {
      addInvalidClass(field);
    } else if (isRecommendedInvalid(field, v)) {
      addInvalidRecommendedClass(field);
    } else {
      addValidClass(field);
    }
    if (props.onBlur) {
      props.onBlur(field.field, v);
    }
  };

  const getValue = (field) => {
    return _.get(props.data, field);
  };

  const buildText = (id) => {
    const saveChanges =
      props.parentProps && props.parentProps[props.primaryField];
    return buildSaveDiscardTextHelper(
      id,
      saveChanges,
      props.saveChangesDialogText,
      props.discardText
    );
  };

  const buildRecommendedText = () => {
    const fields = invalidRecommended.map((el) => el.title);
    return `Máte nevyplnené odporúčané ${fields.length === 1 ? "pole" : "polia"
      }: ${fields.toString()}. Chcete uložiť zmeny?`;
  };

  const handleConfirmRecommended = () => {
    prepareSubmit();
    handleCloseRecommended();
  };

  const handleCloseRecommended = () => {
    setInvalidRecommended([]);
  };

  const ids = props.data ? props.data[getPrimaryField()] : null;
  return (
    <React.Fragment>
      <Detail
        ref={ref}
        handleSubmit={handleSubmit}
        schema={props.schema}
        data={props.data}
        onAdd={props.onAdd}
        onGet={props.onGet}
        getValue={getValue}
        onChange={props.onChange}
        handleBlur={handleBlur}
        onUpload={props.onUpload}
        parentProps={props.parentProps}
        disabled={props.disabled}
        akcDisabled={props.akcDisabled}
        akcHidden={props.akcHidden}
        isLoading={props.isLoading}
        ui={props.ui}
        args={props.args}
        toolbarActions={props.toolbarActions}
        primaryField={props.primaryField}
        toolbarHidden={props.toolbarHidden}
        previous={tryPrevious}
        next={tryNext}
        disabledNext={disabledNext()}
        disabledPrevious={disabledPrevious()}
        currentEl={props.currentEl}
        pageTitle={props.pageTitle}
        onBack={handleBack}
        isPicker={props.isPicker}
        hiddenBack={props.hiddenBack}
        title={props.title}
        pages={props.pages}
        hiddenSave={props.hiddenSave}
        hiddenPrint={props.hiddenPrint}
        dataInvalid={dataInvalid}
        invalidFields={invalidFields}
        sidebarHorizontal={props.sidebarHorizontal}
        children={props.children}
        withMeta={props.withMeta}
        db={props.db}
        componentFactory={props.componentFactory}
        akcFactory={props.akcFactory}
        showAbort={props.showAbort}
        reducer={props.reducer}
      />
      {isOpenDiscard && (
        <ConfirmDialog
          isOpen={isOpenDiscard}
          onClose={handleCloseDiscard}
          onConfirm={handleConfirmDiscard}
          text={buildText(ids)}
          confirmText="Áno"
          cancelText="Nie"
        />
      )}
      {isOpenSaveChangesDialog && (
        <ConfirmDialog
          isOpen={isOpenSaveChangesDialog}
          onClose={handleCloseSaveChangesDialog}
          onCancel={handleCancelSaveChangesDialog}
          onConfirm={handleConfirmSaveChangesDialog}
          text={buildText(ids)}
          confirmText="Uložiť"
          cancelText="Zahodiť zmeny"
        />
      )}
      <ConfirmDialog
        isOpen={invalidRecommended.length !== 0}
        onClose={handleCloseRecommended}
        onConfirm={handleConfirmRecommended}
        text={buildRecommendedText()}
        confirmText="Uložiť"
      />
    </React.Fragment>
  );
});

DetailWrapper.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  schema: PropTypes.array,
  data: PropTypes.object,
  onChange: PropTypes.func,
  onDiscard: PropTypes.func,
  onBlur: PropTypes.func,
  parentProps: PropTypes.object,
  disabled: PropTypes.bool,
  akcDisabled: PropTypes.number,
  akcHidden: PropTypes.number,
  isLoading: PropTypes.bool.isRequired,
  ui: PropTypes.array,
  args: PropTypes.object,
  toolbarActions: PropTypes.any,
  primaryField: PropTypes.string.isRequired,
  onAdd: PropTypes.func,
  onCreate: PropTypes.func,
  onUpdate: PropTypes.func.isRequired,
  onUpload: PropTypes.func,
  toolbarHidden: PropTypes.bool,
  onGet: PropTypes.func.isRequired,
  allData: PropTypes.array.isRequired,
  currentEl: PropTypes.element,
  next: PropTypes.func,
  prev: PropTypes.func,
  modified: PropTypes.bool,
  discardText: PropTypes.string,
  saveChangesDialogText: PropTypes.string,
  isPicker: PropTypes.bool,
  hiddenBack: PropTypes.bool,
  title: PropTypes.string,
  pages: PropTypes.arrayOf(PropTypes.object),
  hiddenSave: PropTypes.bool,
  sidebarHorizontal: PropTypes.bool,
  withMeta: PropTypes.bool,
  componentFactory: PropTypes.func,
  db: PropTypes.string
};

export default DetailWrapper;
